// React Hooks
import React from "react";

// LIBRERIE/IMPORTS
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// LIBRERIE ESTERNE
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBath, faBed, faExpandArrowsAlt, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

// CONFIGURAZIONI
import { AGENCY_ID } from "../../config/appConfig";

// HOOKS
import { useGetProperties } from "../../hooks/useGetProperties";

// COMPONENTI
import LoadingIndicator from "../Loaders/LoadingIndicator";

// Interfaccie Tipi
interface Vetrina2Props {
    agencyName: string;
}

const Vetrina2: React.FC<Vetrina2Props> = ({ agencyName }) => {
    const { t } = useTranslation();
    const { properties, loading, error } = useGetProperties(AGENCY_ID, {
        limit: 6,
        offset: 0,
        withCount: false,
        filters: {
            vetrina: 2
        },
    }
    );



    // ---- Gestione Stati Iniziali ----
    if (loading) return <LoadingIndicator />;

    if (error) {
        console.error("Errore caricamento Vetrina:", error);
        return <div className="alert alert-warning">{t('common.loadingError', 'Errore caricamento dati.')}</div>;
    }

    if (!properties || properties.length === 0) {
        return (
            <div className="text-center p-5">{t('propertyList.notFound.title')}</div>
        )
    }

    // ---- Preparazione Variabili Comuni ----
    const fallbackImagePath = "/assets/images/no_image_big.jpg";

    return (
        <section id="vetrina2-box" className="space-ptb">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 text-center mb-4">
                        <h2>{t('homePage.vetrina.title')}</h2>
                        <p>{t('homePage.vetrina.subTitle')}</p>
                    </div>
                    {/* Utilizza VetrinaProperty per il tipo e slice/map */}
                    {(properties)?.slice(0, 6).map((immobile) => {

                        // --- Preparazione Variabili per il Loop ---
                        const propertyUrl = `/immobile/${immobile.id}`;
                        const locationName = immobile.addressDetails.quartiereName ?? immobile.addressDetails.frazioneName ?? immobile.addressDetails.comuneName ?? t('common.locationUnknown', 'Località sconosciuta');
                        const propertyTitle = `${immobile.tipologyDetails.name} in ${immobile.contractDetails.name} ${locationName == 'Centro' ? 'in' : 'a'} ${locationName}`;
                        const linkTitle = t('property.detailsTitlePattern', { propertyName: propertyTitle });
                        const imageUrl = immobile.mainImage || fallbackImagePath;
                        const imageAlt = (immobile.descriptions && immobile.descriptions[0]?.text.trim() !== "")
                            ? immobile.descriptions[0]?.text.substring(0, 100)
                            : `${propertyTitle} - ${agencyName}`;
                        const contractKey = `property.contractTypes.${immobile.contractDetails.name}`;
                        const contractName = t(contractKey, { defaultValue: immobile.contractDetails.name });
                        const roomCount = immobile.rooms ?? 0;
                        const bathroomCount = immobile.bathrooms ?? 0;

                        // --- Inizio JSX Card ---
                        return (
                            <div key={immobile.id} className="col-md-6 col-lg-4 mb-4">
                                <Link to={propertyUrl} title={linkTitle ?? ''} className="property-item property-card d-block text-body">

                                    {/* Immagine */}
                                    <div className="property-image bg-overlay-gradient-04">
                                        <img
                                            className="img-fluid"
                                            src={imageUrl}
                                            alt={imageAlt ?? ''}
                                            loading="lazy"
                                            onError={(e) => {
                                                if (e.currentTarget.src !== fallbackImagePath) {
                                                    e.currentTarget.src = fallbackImagePath;
                                                }
                                            }}
                                        />
                                        {/* Badge Contratto */}
                                        <div className="property-lable">
                                            <span className="badge badge-md badge-primary">
                                                {contractName}
                                            </span>
                                        </div>
                                        {/* Badge Esclusiva */}
                                        {immobile.exclusive && (
                                            <div className="property-esclusiva-box">
                                                <span className="property-esclusiva">{t('property.exclusive')}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Dettagli Testuali */}
                                    <div className="property-details p-3">
                                        <div className="property-details-inner">
                                            {/* Titolo */}
                                            <h5 className="property-title mb-1">
                                                {propertyTitle}
                                            </h5>
                                            {/* Indirizzo */}
                                            <p className="property-address text-muted mb-2 small">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" />
                                                {immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName ?
                                                    `${immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName}, ${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                    : immobile.addressDetails.comuneName ?
                                                        `${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                        : immobile.addressDetails.provinciaName ?? ''
                                                }
                                            </p>
                                            {/* Riferimento e Prezzo */}
                                            <div className="property-ref-price d-flex justify-content-between align-items-center">
                                                <span className="property-rif text-muted small">
                                                    {t('property.reference')}: {immobile.ref}
                                                </span>
                                                <div className="property-price font-weight-bold">
                                                    {immobile.priceDetails.reservedNegotiation
                                                        ? t('property.priceReserved')
                                                        : `€ ${new Intl.NumberFormat('it-IT').format(immobile.priceDetails.price)} ${immobile.priceDetails.negotiable ? t('common.negotiable') : ""}`.trim()}
                                                </div>
                                            </div>
                                            {/* Info Camere/Bagni/Mq */}
                                            <ul className="property-info list-unstyled d-flex justify-content-around border-top">
                                                {/* Camere (mostra solo se > 0) */}
                                                {roomCount > 0 && (
                                                    <li className="property-bed d-flex align-items-center flex-column" title={`${roomCount} ${t('common.room', { count: roomCount })}`}>
                                                        <FontAwesomeIcon icon={faBed} className="mb-1 text-muted" />
                                                        <span>{immobile.rooms === 1 ? `1 ${t('common.room') || ""}` : `${immobile.rooms} ${t('common.rooms') || ""}`}</span>
                                                    </li>
                                                )}
                                                {/* Bagni (mostra solo se > 0) */}
                                                {bathroomCount > 0 && (
                                                    <li className="property-bath d-flex align-items-center flex-column" title={`${bathroomCount} ${t('common.bathroom', { count: bathroomCount })}`}>
                                                        <FontAwesomeIcon icon={faBath} className="mb-1 text-muted" />
                                                        <span>{immobile.bathrooms === 1 ? `1 ${t('common.bathroom') || ""}` : `${immobile.bathrooms} ${t('common.bathrooms') || ""}`}</span>
                                                    </li>
                                                )}
                                                {/* Mq (mostra solo se > 0) */}
                                                {(immobile.sqmt ?? 0) > 0 && (
                                                    <li className="property-m-sqft d-flex align-items-center flex-column" title={`${immobile.sqmt} m²`}>
                                                        <FontAwesomeIcon icon={faExpandArrowsAlt} className="mb-1 text-muted" />
                                                        <span className="small">{immobile.sqmt} m<sup>2</sup></span>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section >
    );
};

export default Vetrina2;